import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAhuParams, useConstructionClasses } from '../../dataHooks';
import {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useLazyGetProjectByIdQuery,
} from '../../services';
import { useBotContext } from '../useBotContext';
import Select from '../atom/Select';
import Input from '../atom/Input';
import {
  bot_sa_configurations,
  bot_tunnel_configurations,
  bot_tunnel_configurations_all,
  applyFanSectionRules,
  applyOtherSectionRules,
  applyAddReturnAirflowRules,
  applyAddOutsideAirflowRules,
  createBotSection,
} from '../../helpers/data/bot';
import {
  updateAirSystemVolumes,
  updateTunnelConfiguration,
  validateAndUpdateSAConfiguration,
  hasReturnAirTunnel,
  hasExhaustAirTunnel,
  hasSupplyAirTunnel,
} from '../../helpers/bot/botAHUForm.utils';
import { setProjectsOptions } from '../../slices';
import { parseValue } from '../../helpers/index';
import { IBotData, ISelectOption, ISelectValue } from '../../models';
import type { IState } from '../../../../models';
import './styles.scss';

const BotAHUForm: FC<{ setIsLoading: (val: boolean) => void }> = ({
  setIsLoading,
}) => {
  const dispatch = useDispatch();
  const { value: data, setValue } = useBotContext();
  const { data: construction_classes } = useConstructionClasses();
  const { data: pinnedProjectsData } = useGetProjectsQuery({ pin: true });
  const [
    getNormalProjects,
    { data: projectsData, isLoading: isLoadingProjects },
  ] = useLazyGetProjectsQuery();

  const [normalProjectsPage, setNormalProjectsPage] = useState(1);
  const [normalProjectsSearch, setNormalProjectsSearch] = useState('');
  const [normalProjects, setNormalProjects] = useState<any[]>([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);

  const searchTimeoutRef = useRef<number | null>(null);
  const projectIdSelectRef = useRef<any>(null);
  const isFirstRun = useRef(true);
  const didInit = useRef(false);
  const isFirstSearchRun = useRef(true);

  const currentQueryRef = useRef({ page: 1, search: '' }); // TRACK CURRENT QUERY

  const {
    params,
    get: getAhuParams,
    isLoading,
  } = useAhuParams({
    fetch: false,
    includeExtraData: false,
    construction_class_id: data.construction_class_id,
  });

  const { voltages } = useSelector((state: IState) => state.ahu);
  const projectsById = useSelector(
    (state: IState) => state.projects.projectsById,
  );
  const projects = useSelector(
    (state: IState) => state.projects.projects_options,
  );

  const [fetchProjectById] = useLazyGetProjectByIdQuery();

  const asyncLoadProjectOptionById = useCallback(
    async (id: number | string | null): Promise<ISelectOption | null> => {
      if (!id) return null;
      try {
        const project = await fetchProjectById(Number(id)).unwrap();
        if (!project) return null;
        return {
          id: project.id,
          label: project.name,
          value: project.id,
        };
      } catch {
        return null;
      }
    },
    [fetchProjectById],
  );

  useEffect(
    () => setIsLoading(isLoading || isLoadingProjects),
    [isLoading, isLoadingProjects],
  );

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    currentQueryRef.current = { page: 1, search: '' };
    getNormalProjects({ page: 1 });
  }, []);

  // SEARCH INPUT WITH DEBOUNCE
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return; // skip first run
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = window.setTimeout(() => {
      const page = 1;
      currentQueryRef.current = { page, search: normalProjectsSearch };
      setNormalProjectsPage(page);
      getNormalProjects({ page, name: normalProjectsSearch || undefined });
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [normalProjectsSearch]);

  // LOAD MORE PROJECTS ON PAGE CHANGE
  useEffect(() => {
    if (normalProjectsPage > 1) {
      currentQueryRef.current = {
        page: normalProjectsPage,
        search: normalProjectsSearch,
      };
      getNormalProjects({
        page: normalProjectsPage,
        name: normalProjectsSearch || undefined,
      });
    }
  }, [normalProjectsPage, normalProjectsSearch]);

  // UPDATE NORMAL PROJECTS ONLY IF RESPONSE MATCHES CURRENT QUERY
  useEffect(() => {
    if (!projectsData) return;

    if (
      currentQueryRef.current.page === normalProjectsPage &&
      currentQueryRef.current.search === normalProjectsSearch
    ) {
      setNormalProjects((prev) =>
        normalProjectsPage === 1 ? projectsData : [...prev, ...projectsData],
      );
      setHasMoreProjects(projectsData.length === 100);
    }
  }, [projectsData, normalProjectsPage, normalProjectsSearch]);

  // SYNC SELECTED PROJECT
  useEffect(() => {
    if (!data.project_id) return;
    const project = projectsById[data.project_id];
    if (!project) return;
    setSelectedProject({
      id: project.id,
      label: project.name,
      value: project.id,
    });
  }, [data.project_id, projectsById]);

  const handleScrollToBottom = useCallback(() => {
    if (!isLoadingProjects && hasMoreProjects) {
      setNormalProjectsPage((prev) => prev + 1);
    }
  }, [isLoadingProjects, hasMoreProjects]);

  const handleSearchInputChange = useCallback(
    (inputValue: string, meta?: { action?: string }) => {
      if (meta?.action !== 'input-change') return;
      setNormalProjectsSearch(inputValue);
    },
    [],
  );

  useEffect(() => {
    if (!construction_classes) return;
    getAhuParams(data.construction_class_id);
    setValue((draft: IBotData) => {
      draft.floor_construction_id = construction_classes?.find(
        (cc) => cc.id === data.construction_class_id,
      )?.floor_construction_id as number;
    });
  }, [data.construction_class_id, construction_classes]);

  useEffect(() => {
    if (!params) return;
    setValue((draft) => {
      if (
        draft.weather_rating_id &&
        !params.weather_rating.find((w) => w.id === draft.weather_rating_id)
      ) {
        draft.weather_rating_id = params.weather_rating[0]?.id || null;
      }
    });
  }, [params]);

  useEffect(() => {
    projectIdSelectRef.current?.focus();
  }, []);

  useEffect(() => {
    setValue((draft: IBotData) => {
      draft.hasSupplyAir = hasSupplyAirTunnel(draft);
      draft.hasReturnAir = hasReturnAirTunnel(draft);
      draft.hasExhaustAir = hasExhaustAirTunnel(draft);
      updateAirSystemVolumes(draft, draft.air_system_id ?? 1);
    });
  }, [
    data.air_system_id,
    data.sa_volume_max,
    data.tunnel_configuration,
    data.sa_ea_intake_configuration,
    setValue,
  ]);

  useEffect(() => {
    setValue((draft: IBotData) => {
      applyOtherSectionRules(draft);
      applyFanSectionRules(draft);
      applyAddReturnAirflowRules(draft);
      applyAddOutsideAirflowRules(draft);
    });
  }, [
    data.air_system_id,
    data.tunnel_configuration,
    data.sa_ea_intake_configuration,
    data.ea_volume_max,
    data.ra_volume_max,
    data.sa_volume_max,
    setValue,
  ]);

  const handleInputChange = useCallback(
    (val: string | number, name?: string) => {
      if (!name) return;
      setValue((draft: IBotData) => {
        (draft as unknown as Record<string, string | number>)[name] = val;
      });
    },
    [setValue],
  );

  const handleAirSystemChange = useCallback(
    (draft: IBotData, airSystemId: number) => {
      updateAirSystemVolumes(draft, airSystemId);
      applyOtherSectionRules(draft);
      applyFanSectionRules(draft);
      applyAddReturnAirflowRules(draft);
      applyAddOutsideAirflowRules(draft);
    },
    [],
  );

  const handleTunnelConfigurationChange = useCallback(
    (draft: IBotData, tunnelConfig: number) => {
      updateTunnelConfiguration(draft, tunnelConfig);
      validateAndUpdateSAConfiguration(draft);
      applyOtherSectionRules(draft);
      applyFanSectionRules(draft);
      applyAddReturnAirflowRules(draft);
      applyAddOutsideAirflowRules(draft);
    },
    [],
  );

  const handleSAConfigurationChange = useCallback(
    (draft: IBotData, parsedValue: string | number) => {
      const airSystemId = draft.air_system_id ?? 1;
      const tunnelConfig = draft.tunnel_configuration ?? 1;
      const config = (bot_sa_configurations as any)[airSystemId]?.[
        tunnelConfig
      ];
      const allowedSA = config?.sa_ea_configurations ?? [];
      const selectedValue =
        typeof parsedValue === 'string'
          ? parsedValue
          : draft.sa_ea_intake_configuration;
      if (
        allowedSA.length &&
        !allowedSA.some((opt: { value: string }) => opt.value === selectedValue)
      ) {
        draft.sa_ea_intake_configuration = allowedSA[0].value;
      }
      applyOtherSectionRules(draft);
      applyFanSectionRules(draft);
      applyAddReturnAirflowRules(draft);
      applyAddOutsideAirflowRules(draft);
    },
    [],
  );

  const handleSelectChange = useCallback(
    (
      value: ISelectValue,
      name: string,
      _fieldType?: string,
      _label?: string,
    ) => {
      if (!name) return;
      const parsed = parseValue(value);
      let check_add_plenum = false;
      setValue((draft: IBotData) => {
        (draft as unknown as Record<string, string | number>)[name] = parsed;
        if (name === 'air_system_id') {
          const airSystemId =
            typeof parsed === 'number' ? parsed : (draft.air_system_id ?? 1);
          handleAirSystemChange(draft, airSystemId);
          if (parsed === 2) {
            check_add_plenum = true;
          }
        } else if (name === 'tunnel_configuration') {
          const tunnelConfig = typeof parsed === 'number' ? parsed : 1;
          handleTunnelConfigurationChange(draft, tunnelConfig);
          check_add_plenum = draft.air_system_id === 2;
        } else if (name === 'sa_ea_intake_configuration') {
          handleSAConfigurationChange(draft, parsed);
          check_add_plenum = draft.air_system_id === 2;
        } else if (name === 'voltage_id') {
          draft.voltage_name = _label || '';
        } else if (name === 'project_id') {
          setSelectedProject({
            id: parsed,
            label: _label,
            value: parsed,
          });
          setNormalProjectsSearch('');
          setNormalProjectsPage(1);
        }
        if (check_add_plenum) {
          draft.tunnels.forEach((tunnel) => {
            if (tunnel.sections[0]?.type !== 'plenum') {
              tunnel.sections.unshift(createBotSection({ type: 'plenum' }));
            }
          });
        }
      });
    },
    [
      setValue,
      handleAirSystemChange,
      handleTunnelConfigurationChange,
      handleSAConfigurationChange,
    ],
  );

  const tunnel_configurations = useMemo(() => {
    return data.air_system_id !== 5
      ? bot_tunnel_configurations
      : bot_tunnel_configurations_all;
  }, [data.air_system_id]);

  const sa_configurations = useMemo(
    () =>
      (bot_sa_configurations as any)[data.air_system_id ?? 1]?.[
        data.tunnel_configuration ?? 1
      ]?.sa_ea_configurations ?? [],
    [data.air_system_id, data.tunnel_configuration],
  );

  const projectOptions = useMemo<ISelectOption[]>(() => {
    const pinnedProjects = pinnedProjectsData || [];
    const pinnedIds = new Set(pinnedProjects.map((p) => p.id));
    const normalProjectsFiltered = normalProjects.filter(
      (p) => !pinnedIds.has(p.id),
    );
    let combined = [...pinnedProjects, ...normalProjectsFiltered];

    const map = new Map();
    combined.forEach((p) => map.set(p.id, p));

    if (selectedProject) {
      map.set(selectedProject.id, selectedProject);
    }

    return Array.from(map.values()).map((project) => {
      const label = project.name || project.label;
      return {
        id: project.id,
        label,
        value: project.id,
      };
    });
  }, [pinnedProjectsData, normalProjects, selectedProject]);

  useEffect(() => {
    dispatch(setProjectsOptions(projectOptions));
  }, [projectOptions]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setValue((draft: IBotData) => {
      const currentValue = draft.sa_ea_intake_configuration;
      const isValidValue = sa_configurations?.some(
        (opt: { value: string }) => opt.value === currentValue,
      );
      if (!currentValue || !isValidValue) {
        draft.sa_ea_intake_configuration = sa_configurations?.[0]?.value;
        handleSAConfigurationChange(draft, sa_configurations?.[0]?.value);
      }
    });
  }, [data.tunnel_configuration, setValue, sa_configurations]);

  const getOption = useCallback(
    (option: ISelectOption) => ({
      label: option.label || option.construction,
      value: option.id,
    }),
    [],
  );

  return (
    <div className="bot-ahu">
      <div className="bot-ahu__sentence">
        My project is
        <div className="bot-ahu__select-wrapper bot-ahu__select-wrapper_project">
          <Select
            options={projects}
            value={data.project_id}
            onChange={handleSelectChange}
            name="project_id"
            className="bot-ahu__select"
            isSearchable={true}
            border_bottom={!data.project_id}
            required
            onScroll={handleScrollToBottom}
            onInputChange={handleSearchInputChange}
            asyncLoadOptionById={asyncLoadProjectOptionById}
          />
        </div>
        . My air handling unit tag is
        <div className="bot-ahu__input-wrapper">
          <Input
            name="tag"
            type="text"
            placeholder="Enter AHU tag"
            onChange={handleInputChange}
            value={data.tag}
            className="bot-ahu__input"
            autoResize
            required
          />
        </div>
      </div>
      <div className="bot-ahu__sentence">
        The unit construction class is
        <div className="bot-ahu__select-wrapper">
          <Select
            options={construction_classes}
            value={data.construction_class_id}
            onChange={handleSelectChange}
            name="construction_class_id"
            className="bot-ahu__select"
            border_bottom={false}
            required
            getLabelAndValue={getOption}
          />
        </div>
        , it's
        <div className="bot-ahu__select-wrapper">
          <Select
            options={params?.weather_rating}
            value={data.weather_rating_id}
            onChange={handleSelectChange}
            className="bot-ahu__select"
            isLowerCase={true}
            name="weather_rating_id"
            border_bottom={false}
            required
          />
        </div>
        &nbsp;rated,
        <div className="bot-ahu__select-wrapper">
          <Select
            options={params?.air?.air_system
              ?.filter((e) => e.id !== 1)
              .reverse()}
            value={data.air_system_id}
            onChange={handleSelectChange}
            className="bot-ahu__select"
            name="air_system_id"
            border_bottom={false}
            required
          />
        </div>
      </div>
      <div className="bot-ahu__sentence">
        The unit configuration is
        <div className="bot-ahu__select-wrapper">
          <Select
            options={tunnel_configurations}
            value={data.tunnel_configuration}
            onChange={handleSelectChange}
            className="bot-ahu__select"
            name="tunnel_configuration"
            isLowerCase={true}
            border_bottom={false}
            required
          />
        </div>
        , with the
        <div className="bot-ahu__select-wrapper flex-1">
          <Select
            name="sa_ea_intake_configuration"
            options={sa_configurations}
            value={data.sa_ea_intake_configuration}
            onChange={handleSelectChange}
            className="bot-ahu__select"
            isLowerCase={true}
            border_bottom={false}
            required
          />
        </div>
      </div>
      <div className="bot-ahu__sentence">
        {data.hasSupplyAir !== false && (
          <>
            The supply airflow is
            <div className="bot-ahu__input-wrapper">
              <Input
                name="sa_volume_max"
                type="number"
                numberSeparator
                placeholder="__"
                onChange={handleInputChange}
                value={data.sa_volume_max}
                className="bot-ahu__input"
                autoResize
                required
                max={400000}
              />
            </div>
            CFM
          </>
        )}
        {data.hasOutsideAir && (
          <>
            {data.hasSupplyAir !== false ? ', t' : 'T'}he outside airflow is
            <div className="bot-ahu__input-wrapper">
              <Input
                name="oa_volume_max"
                type="number"
                numberSeparator
                placeholder="- -"
                onChange={handleInputChange}
                value={data.oa_volume_max ?? ''}
                className="bot-ahu__input"
                autoResize
                required
                max={400000}
              />
            </div>
            CFM
          </>
        )}
        {data.hasReturnAir && (
          <>
            {data.hasSupplyAir !== false ? ', t' : 'T'}he return airflow is
            <div className="bot-ahu__input-wrapper">
              <Input
                name="ra_volume_max"
                type="number"
                numberSeparator
                placeholder="- -"
                onChange={handleInputChange}
                value={data.ra_volume_max ?? ''}
                className="bot-ahu__input"
                autoResize
                required
                max={400000}
              />
            </div>
            CFM
          </>
        )}
        {data.hasExhaustAir && (
          <>
            {data.hasSupplyAir !== false ? ', t' : 'T'}
            he exhaust airflow is
            <div className="bot-ahu__input-wrapper">
              <Input
                name="ea_volume_max"
                type="number"
                numberSeparator
                placeholder="- -"
                onChange={handleInputChange}
                value={data.ea_volume_max ?? ''}
                className="bot-ahu__input"
                autoResize
                required
                max={400000}
              />
            </div>
            CFM
          </>
        )}
        . The unit power voltage is
        <div className="bot-ahu__select-wrapper">
          <Select
            name="voltage_id"
            options={voltages?.filter(
              (el) =>
                el.name === '208/3/60' ||
                el.name === '230/3/60' ||
                el.name === '460/3/60',
            )}
            value={data.voltage_id}
            onChange={handleSelectChange}
            className="bot-ahu__select"
            border_bottom={false}
            required
          />
        </div>
      </div>
    </div>
  );
};
export default BotAHUForm;
